import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import {useTolgee, useTranslate} from "@tolgee/react";

export type PreviewTranslate = (key: string, defaultValue?: string) => string;

const PreviewTranslateContext = createContext<PreviewTranslate | null>(null);

type Props = {
  languageCode?: string;
};

/**
 * Binds `t` to the language being previewed rather than the one this console
 * runs in: the static platform copy inside an email body has to read the way
 * the recipient will get it, next to the organiser's own translated text.
 *
 * Tolgee keeps only the active language's record in memory, so the previewed
 * one is pulled in first — until it lands, the console's language is used, and
 * missing keys still fall back the usual way.
 */
export const PreviewTranslateProvider: React.FC<Props> = ({
  languageCode,
  children,
}) => {
  const tolgee = useTolgee();
  const {t} = useTranslate();
  const [loadedLanguage, setLoadedLanguage] = useState<string>();

  useEffect(() => {
    if (!languageCode) return undefined;

    let cancelled = false;

    tolgee
      .loadRecord({language: languageCode})
      .catch((e) => console.error("Failed to load preview translations: ", e))
      .finally(() => {
        if (!cancelled) setLoadedLanguage(languageCode);
      });

    return () => {
      cancelled = true;
    };
  }, [tolgee, languageCode]);

  const translate = useCallback<PreviewTranslate>(
    (key, defaultValue) =>
      languageCode && loadedLanguage === languageCode
        ? t(key, defaultValue, {language: languageCode})
        : t(key, defaultValue),
    [t, languageCode, loadedLanguage]
  );

  return (
    <PreviewTranslateContext.Provider value={translate}>
      {children}
    </PreviewTranslateContext.Provider>
  );
};

/** Outside a preview this is just the console's own `t`. */
export const usePreviewTranslate = (): PreviewTranslate => {
  const preview = useContext(PreviewTranslateContext);
  const {t} = useTranslate();

  return preview ?? t;
};

export default PreviewTranslateProvider;
